import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Book, DedicationSettings } from '../types';
import { getGraphQLClient, GET_BOOK, UPDATE_BOOK } from '../lib/graphql';
import { captureException } from '../lib/sentry';
import { FrontMatterEditorLayout } from '../components/front-matter/FrontMatterEditorLayout';
import {
  getDedicationTemplates,
  getDedicationTemplate,
  DedicationTemplate,
} from '../lib/frontMatterTemplates';

export default function DedicationEditorPage() {
  const { t } = useTranslation();
  const { bookId } = useParams<{ bookId: string }>();
  const [book, setBook] = useState<Book | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [isVisible, setIsVisible] = useState(false);
  const [text, setText] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  useEffect(() => {
    const loadBook = async () => {
      if (!bookId) return;

      try {
        const client = getGraphQLClient();
        const data = await client.request<{ book: Book }>(GET_BOOK, { id: bookId });
        setBook(data.book);

        // Initialize form state from book settings
        const settings = data.book.frontMatterSettings?.dedication;
        setIsVisible(settings?.visible ?? false);
        setText(settings?.text ?? '');
        setSelectedTemplateId(settings?.templateId ?? null);
      } catch (err) {
        captureException(err);
        setError(t('common.error'));
      } finally {
        setIsLoading(false);
      }
    };

    loadBook();
  }, [bookId, t]);

  const handleSave = async () => {
    if (!bookId) return;

    setIsSaving(true);
    setError(null);

    try {
      const client = getGraphQLClient();
      const dedicationSettings: DedicationSettings = {
        visible: isVisible,
        text: text || undefined,
        templateId: selectedTemplateId || undefined,
      };

      await client.request(UPDATE_BOOK, {
        id: bookId,
        frontMatterSettings: {
          dedication: dedicationSettings,
        },
      });
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const handleTemplateSelect = (template: DedicationTemplate) => {
    setText(t(template.contentKey));
    setSelectedTemplateId(template.id);
  };

  const handleTextChange = (newText: string) => {
    setText(newText);
    // Clear template selection if user edits text
    if (selectedTemplateId) {
      const template = getDedicationTemplate(selectedTemplateId);
      if (template && newText !== t(template.contentKey)) {
        setSelectedTemplateId(null);
      }
    }
  };

  const templates = book ? getDedicationTemplates(book.bookType) : [];

  return (
    <FrontMatterEditorLayout
      title={t('frontMatter.dedication')}
      bookId={bookId!}
      isLoading={isLoading}
      isSaving={isSaving}
      isVisible={isVisible}
      onVisibilityChange={setIsVisible}
      onSave={handleSave}
      error={error}
      preview={
        <div className="text-center">
          {text ? (
            <p className="font-serif text-lg italic text-gray-700 dark:text-gray-300 max-w-sm leading-relaxed">
              {text}
            </p>
          ) : (
            <p className="text-gray-400 dark:text-gray-500 italic">
              {t('frontMatter.noDedication')}
            </p>
          )}
        </div>
      }
    >
      <div className="space-y-6">
        {/* Template selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('frontMatter.templates.title')}
          </label>
          <div className="grid grid-cols-1 gap-2">
            {templates.map((template) => (
              <button
                key={template.id}
                onClick={() => handleTemplateSelect(template)}
                className={`text-left px-4 py-3 rounded-lg border transition-colors ${
                  selectedTemplateId === template.id
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <p className="text-sm text-gray-700 dark:text-gray-300 italic">
                  &ldquo;{t(template.contentKey)}&rdquo;
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Custom text */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('frontMatter.customText')}
          </label>
          <textarea
            value={text}
            onChange={(e) => handleTextChange(e.target.value)}
            placeholder={t('frontMatter.dedicationPlaceholder')}
            rows={4}
            className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
          />
        </div>
      </div>
    </FrontMatterEditorLayout>
  );
}
